import { AppImage } from '@/components/ui/app-image';
import { Rating } from '@/components/ui/rating';
import { Rating as RatingDistributionDisplay } from '@/components/ui/rating-distribution';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import type { RatingDistribution } from '@/types/feed';
import { ratingKeyToIndex } from '@/utils/rating-utils';
import React from 'react';
import { Pressable, View } from 'react-native';

interface CheckInPreviewData {
  checkInId: string;
  userName: string;
  userAvatar?: string;
  menuItemName: string;
  businessName: string;
  ratingKey?: string;
  occurredAt?: string;
  location?: string;
}

interface MenuItemPreviewData {
  menuItemId: string;
  menuItemName: string;
  businessName: string;
  coverPhoto?: string;
  checkInCount?: number;
  ratingDistribution?: RatingDistribution;
}

interface ListPreviewData {
  listId: string;
  listName: string;
  coverImage?: string;
  ownerName?: string;
  itemCount?: number;
  memberCount?: number;
}

type PreviewCardProps =
  | { variant: 'check_in'; data: CheckInPreviewData; onPress: () => void }
  | { variant: 'menu_item'; data: MenuItemPreviewData; onPress: () => void }
  | { variant: 'list'; data: ListPreviewData; onPress: () => void };

function formatCompactTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days}d`;
  if (days < 28) return `${Math.floor(days / 7)}w`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const CheckInPreview = React.memo(function CheckInPreview({
  data,
  onPress,
}: {
  data: CheckInPreviewData;
  onPress: () => void;
}) {
  const ratingIndex = data.ratingKey ? ratingKeyToIndex(data.ratingKey) : null;

  return (
    <Pressable onPress={onPress} className="bg-card border border-border rounded-xl p-3 mt-2">
      <View className="flex-row items-center">
        <UserAvatar uri={data.userAvatar} name={data.userName} size={36} decorative />
        <View className="flex-1 mx-3">
          <Text className="text-sm text-muted-foreground">
            {data.userName} rated{' '}
            <Text className="text-foreground font-bold">{data.menuItemName}</Text>
            {' '}at{' '}
            <Text className="text-foreground font-bold">{data.businessName}</Text>
          </Text>
          <Text className="text-xs text-muted-foreground mt-0.5">
            {data.occurredAt && formatCompactTime(data.occurredAt)}
            {data.occurredAt && data.location && ' · '}
            {data.location}
          </Text>
        </View>
        {ratingIndex !== null && <Rating rating={ratingIndex} />}
      </View>
    </Pressable>
  );
});

const MenuItemPreview = React.memo(function MenuItemPreview({
  data,
  onPress,
}: {
  data: MenuItemPreviewData;
  onPress: () => void;
}) {

  return (
    <Pressable onPress={onPress} className="bg-card border border-border rounded-xl p-3 mt-2">
      <View className="flex-row items-center">
        {data.coverPhoto ? (
          <AppImage
            r2Key={data.coverPhoto}
            preset="thumbnail"
            style={{ width: 48, height: 48, borderRadius: 8 }}
            accessibilityLabel={`Photo of ${data.menuItemName}`}
          />
        ) : (
          <View className="w-12 h-12 rounded-lg bg-muted" />
        )}
        <View className="flex-1 mx-3">
          <Text className="text-sm text-foreground font-bold" numberOfLines={1}>
            {data.menuItemName}
          </Text>
          <Text className="text-xs text-muted-foreground" numberOfLines={1}>
            {data.businessName}
          </Text>
          {data.checkInCount != null && data.checkInCount > 0 && (
            <Text className="text-xs text-muted-foreground">
              {data.checkInCount} {data.checkInCount === 1 ? 'check-in' : 'check-ins'}
            </Text>
          )}
        </View>
        {data.ratingDistribution && (
          <RatingDistributionDisplay distribution={data.ratingDistribution} size="sm" />
        )}
      </View>
    </Pressable>
  );
});

const ListPreview = React.memo(function ListPreview({
  data,
  onPress,
}: {
  data: ListPreviewData;
  onPress: () => void;
}) {

  return (
    <Pressable onPress={onPress} className="bg-card border border-border rounded-xl p-3 mt-2">
      <View className="flex-row items-center">
        {data.coverImage ? (
          <AppImage
            r2Key={data.coverImage}
            preset="thumbnail"
            style={{ width: 48, height: 48, borderRadius: 8 }}
            accessibilityLabel={`Cover of ${data.listName}`}
          />
        ) : (
          <View className="w-12 h-12 rounded-lg bg-muted" />
        )}
        <View className="flex-1 mx-3">
          <Text className="text-sm text-foreground font-bold" numberOfLines={1}>
            {data.listName}
          </Text>
          {data.ownerName && (
            <Text className="text-xs text-muted-foreground" numberOfLines={1}>
              by {data.ownerName}
            </Text>
          )}
        </View>
        <View className="items-end">
          {data.itemCount != null && (
            <Text className="text-xs text-muted-foreground">
              {data.itemCount} {data.itemCount === 1 ? 'item' : 'items'}
            </Text>
          )}
          {data.memberCount != null && data.memberCount > 1 && (
            <Text className="text-xs text-muted-foreground">
              {data.memberCount} {data.memberCount === 1 ? 'member' : 'members'}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
});

export const PreviewCard = React.memo(function PreviewCard(props: PreviewCardProps) {
  if (props.variant === 'check_in') {
    return <CheckInPreview data={props.data} onPress={props.onPress} />;
  }
  if (props.variant === 'menu_item') {
    return <MenuItemPreview data={props.data} onPress={props.onPress} />;
  }
  if (props.variant === 'list') {
    return <ListPreview data={props.data} onPress={props.onPress} />;
  }
  return null;
});
